import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="timezone-detector"
export default class extends Controller {
  static targets = ["select"]

  connect() {
    console.log("TimezoneDetector controller connected")
    this.detectAndSetTimezone()
  }

  detectAndSetTimezone() {
    try {
      // Get the user's timezone from the browser
      const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone
      console.log("Detected browser timezone:", browserTimezone)

      if (!browserTimezone) {
        console.warn("Could not detect browser timezone")
        return
      }

      // Map IANA timezone to Rails timezone
      const railsTimezone = this.mapToRailsTimezone(browserTimezone)
      console.log("Mapped to Rails timezone:", railsTimezone)

      if (railsTimezone) {
        this.setSelectValue(railsTimezone)
      }
    } catch (error) {
      console.error("Error detecting timezone:", error)
    }
  }

  setSelectValue(timezone) {
    const selectElement = this.selectTarget
    
    // Try to find an option that matches the timezone
    const options = Array.from(selectElement.options)
    const matchingOption = options.find(option => {
      return option.value === timezone || option.text.includes(timezone)
    })

    if (matchingOption) {
      selectElement.value = matchingOption.value
      console.log("Set timezone to:", matchingOption.text)
    } else {
      console.warn("Could not find matching option for timezone:", timezone)
    }
  }

  // Map IANA timezone identifiers to Rails timezone names
  mapToRailsTimezone(ianaTimezone) {
    const timezoneMap = {
      // US & Canada
      "America/New_York": "Eastern Time (US & Canada)",
      "America/Detroit": "Eastern Time (US & Canada)",
      "America/Kentucky/Louisville": "Eastern Time (US & Canada)",
      "America/Kentucky/Monticello": "Eastern Time (US & Canada)",
      "America/Indiana/Indianapolis": "Indiana (East)",
      "America/Indiana/Vincennes": "Indiana (East)",
      "America/Indiana/Winamac": "Indiana (East)",
      "America/Indiana/Marengo": "Indiana (East)",
      "America/Indiana/Petersburg": "Indiana (East)",
      "America/Indiana/Vevay": "Indiana (East)",
      "America/Chicago": "Central Time (US & Canada)",
      "America/Indiana/Tell_City": "Central Time (US & Canada)",
      "America/Indiana/Knox": "Central Time (US & Canada)",
      "America/Menominee": "Central Time (US & Canada)",
      "America/North_Dakota/Center": "Central Time (US & Canada)",
      "America/North_Dakota/New_Salem": "Central Time (US & Canada)",
      "America/North_Dakota/Beulah": "Central Time (US & Canada)",
      "America/Denver": "Mountain Time (US & Canada)",
      "America/Boise": "Mountain Time (US & Canada)",
      "America/Phoenix": "Arizona",
      "America/Los_Angeles": "Pacific Time (US & Canada)",
      "America/Anchorage": "Alaska",
      "America/Juneau": "Alaska",
      "America/Sitka": "Alaska",
      "America/Metlakatla": "Alaska",
      "America/Yakutat": "Alaska",
      "America/Nome": "Alaska",
      "America/Adak": "Alaska",
      "Pacific/Honolulu": "Hawaii",
      
      // Canada
      "America/Toronto": "Eastern Time (US & Canada)",
      "America/Montreal": "Eastern Time (US & Canada)",
      "America/Winnipeg": "Central Time (US & Canada)",
      "America/Regina": "Saskatchewan",
      "America/Edmonton": "Mountain Time (US & Canada)",
      "America/Vancouver": "Pacific Time (US & Canada)",
      "America/Halifax": "Atlantic Time (Canada)",
      "America/St_Johns": "Newfoundland",
      
      // Mexico
      "America/Mexico_City": "Mexico City",
      "America/Cancun": "Mexico City",
      "America/Merida": "Mexico City",
      "America/Monterrey": "Monterrey",
      "America/Matamoros": "Central Time (US & Canada)",
      "America/Mazatlan": "Mazatlan",
      "America/Chihuahua": "Chihuahua",
      "America/Hermosillo": "Arizona",
      "America/Tijuana": "Tijuana",
      
      // South America
      "America/Bogota": "Bogota",
      "America/Lima": "Lima",
      "America/Guayaquil": "Quito",
      "America/Caracas": "Caracas",
      "America/La_Paz": "La Paz",
      "America/Santiago": "Santiago",
      "America/Buenos_Aires": "Buenos Aires",
      "America/Montevideo": "Montevideo",
      "America/Sao_Paulo": "Brasilia",
      "America/Fortaleza": "Brasilia",
      "America/Recife": "Brasilia",
      "America/Belem": "Brasilia",
      "America/Manaus": "Brasilia",
      
      // Europe
      "Europe/London": "London",
      "Europe/Dublin": "Dublin",
      "Europe/Lisbon": "Lisbon",
      "Europe/Paris": "Paris",
      "Europe/Brussels": "Brussels",
      "Europe/Amsterdam": "Amsterdam",
      "Europe/Berlin": "Berlin",
      "Europe/Bern": "Bern",
      "Europe/Rome": "Rome",
      "Europe/Madrid": "Madrid",
      "Europe/Vienna": "Vienna",
      "Europe/Prague": "Prague",
      "Europe/Budapest": "Budapest",
      "Europe/Warsaw": "Warsaw",
      "Europe/Stockholm": "Stockholm",
      "Europe/Copenhagen": "Copenhagen",
      "Europe/Oslo": "Copenhagen",
      "Europe/Helsinki": "Helsinki",
      "Europe/Athens": "Athens",
      "Europe/Bucharest": "Bucharest",
      "Europe/Sofia": "Sofia",
      "Europe/Istanbul": "Istanbul",
      "Europe/Kiev": "Kyiv",
      "Europe/Kyiv": "Kyiv",
      "Europe/Minsk": "Minsk",
      "Europe/Moscow": "Moscow",
      "Europe/Volgograd": "Volgograd",
      "Europe/Samara": "Samara",
      "Europe/Kaliningrad": "Kaliningrad",
      "Europe/Riga": "Riga",
      "Europe/Tallinn": "Tallinn",
      "Europe/Vilnius": "Vilnius",
      "Europe/Belgrade": "Belgrade",
      "Europe/Bratislava": "Bratislava",
      "Europe/Ljubljana": "Ljubljana",
      "Europe/Zagreb": "Zagreb",
      "Europe/Sarajevo": "Sarajevo",
      "Europe/Skopje": "Skopje",
      "Europe/Zurich": "Zurich",
      
      // Africa
      "Africa/Cairo": "Cairo",
      "Africa/Johannesburg": "Pretoria",
      "Africa/Harare": "Harare",
      "Africa/Nairobi": "Nairobi",
      "Africa/Lagos": "West Central Africa",
      "Africa/Casablanca": "Casablanca",
      
      // Middle East
      "Asia/Jerusalem": "Jerusalem",
      "Asia/Baghdad": "Baghdad",
      "Asia/Kuwait": "Kuwait",
      "Asia/Riyadh": "Riyadh",
      "Asia/Tehran": "Tehran",
      "Asia/Dubai": "Abu Dhabi",
      "Asia/Muscat": "Muscat",
      "Asia/Baku": "Baku",
      "Asia/Tbilisi": "Tbilisi",
      "Asia/Yerevan": "Yerevan",
      
      // Asia
      "Asia/Karachi": "Karachi",
      "Asia/Kolkata": "Kolkata",
      "Asia/Calcutta": "Kolkata",
      "Asia/Mumbai": "Mumbai",
      "Asia/Chennai": "Chennai",
      "Asia/Kathmandu": "Kathmandu",
      "Asia/Dhaka": "Dhaka",
      "Asia/Colombo": "Sri Jayawardenepura",
      "Asia/Almaty": "Almaty",
      "Asia/Tashkent": "Tashkent",
      "Asia/Yekaterinburg": "Ekaterinburg",
      "Asia/Novosibirsk": "Novosibirsk",
      "Asia/Krasnoyarsk": "Krasnoyarsk",
      "Asia/Irkutsk": "Irkutsk",
      "Asia/Yakutsk": "Yakutsk",
      "Asia/Vladivostok": "Vladivostok",
      "Asia/Magadan": "Magadan",
      "Asia/Kamchatka": "Kamchatka",
      "Asia/Bangkok": "Bangkok",
      "Asia/Hanoi": "Hanoi",
      "Asia/Jakarta": "Jakarta",
      "Asia/Singapore": "Singapore",
      "Asia/Kuala_Lumpur": "Kuala Lumpur",
      "Asia/Hong_Kong": "Hong Kong",
      "Asia/Taipei": "Taipei",
      "Asia/Shanghai": "Beijing",
      "Asia/Chongqing": "Chongqing",
      "Asia/Urumqi": "Urumqi",
      "Asia/Tokyo": "Tokyo",
      "Asia/Seoul": "Seoul",
      "Asia/Pyongyang": "Seoul",
      "Asia/Ulaanbaatar": "Ulaanbaatar",
      
      // Australia & Pacific
      "Australia/Perth": "Perth",
      "Australia/Darwin": "Darwin",
      "Australia/Adelaide": "Adelaide",
      "Australia/Brisbane": "Brisbane",
      "Australia/Sydney": "Sydney",
      "Australia/Melbourne": "Melbourne",
      "Australia/Hobart": "Hobart",
      "Australia/Canberra": "Canberra",
      "Pacific/Auckland": "Auckland",
      "Pacific/Wellington": "Wellington",
      "Pacific/Fiji": "Fiji",
      "Pacific/Guam": "Guam",
      "Pacific/Port_Moresby": "Port Moresby",
      "Pacific/Guadalcanal": "Solomon Is.",
      "Pacific/Noumea": "New Caledonia",
      "Pacific/Tongatapu": "Nuku'alofa",
      "Pacific/Apia": "Samoa",
      "Pacific/Chatham": "Chatham Is.",
      "Pacific/Midway": "Midway Island",
      "Pacific/Pago_Pago": "American Samoa",
      
      // Atlantic
      "Atlantic/Azores": "Azores",
      "Atlantic/Cape_Verde": "Cape Verde Is.",
      "Atlantic/Reykjavik": "Monrovia",
      "Atlantic/Bermuda": "Atlantic Time (Canada)",
    }

    return timezoneMap[ianaTimezone] || null
  }
}

